import axios, { AxiosRequestConfig } from "axios";
import { SERVER_URL } from "../config";

axios.defaults.baseURL = SERVER_URL;
axios.defaults.headers.post["Content-Type"] = "application/json";

// Define types for axios error handling and interceptors
type AxiosError = {
  status?: number;
  response?: {
    data?: {
      detail?: string;
    };
  };
  message?: string;
};

axios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error: AxiosError) {
    let message;
    switch (error.status) {
      case 500:
        message = "Internal Server Error";
        break;
      case 401:
        message = "Invalid credentials";
        if (error.response?.data?.detail === "Could not validate credentials") {
          // Assuming `this.navigate` is defined somewhere
          // this.navigate("/jumpin");
        }
        break;
      case 403:
        message = "Not Authenticated";
        if (error.response?.data?.detail === "Not authenticated") {
          // this.navigate("/jumpin");
        }
        break;
      case 404:
        message = "Sorry! The data you are looking for could not be found";
        break;
      default:
        message = error.response?.data?.detail || error.message || error;
    }
    return Promise.reject(message);
  },
);

const setAuthorizationHeader = (authToken: string | null) => {
  console.log("Setting authorization header...");
  if (authToken) {
    axios.defaults.headers.common["Authorization"] = "Bearer " + authToken;
  }
};

class APIClient {
  controller: AbortController | undefined;
  navigate: any; // Define type for navigate function if needed

  constructor() {
    this.controller = undefined;
    this.navigate = undefined;
  }

  setNavigate = (navigateFunction: any) => {
    this.navigate = navigateFunction;
  };

  get = async (url: string, params?: AxiosRequestConfig["params"]) => {
    this.controller = new AbortController();

    try {
      const response = await axios.get(url, {
        params,
        signal: this.controller?.signal,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  post = async (url: string, data?: any) => {
    this.controller = new AbortController();

    try {
      const response = await axios.post(url, data, {
        signal: this.controller?.signal,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  postWithParams = async (
    url: string,
    params: AxiosRequestConfig["params"],
    data?: any,
  ) => {
    this.controller = new AbortController();

    try {
      const response = await axios.post(url, data, {
        params,
        signal: this.controller?.signal,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  postForFiles = async (url: string, data: FormData) => {
    this.controller = new AbortController();

    try {
      const response = await axios.post(url, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        signal: this.controller?.signal,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  update = async (url: string, data?: any) => {
    try {
      const response = await axios.patch(url, data, {
        signal: this.controller?.signal,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  put = async (url: string, data?: any) => {
    try {
      const response = await axios.put(url, data, {
        signal: this.controller?.signal,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  delete = async (url: string, config?: AxiosRequestConfig) => {
    try {
      const response = await axios.delete(url, {
        ...config,
        signal: this.controller?.signal,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  cancel = () => {
    if (this.controller) {
      this.controller.abort();
    }
  };
}

export { APIClient, setAuthorizationHeader };
